/**
 * The lights the volumetric fog scatters, packed as Godot's light buffers hold them for the fog
 * pass (`storage_rd/light_storage.cpp:636-760,840-1029`, 4.7.2 `:1054-1094` for the area light):
 * view-space positions and directions, a colour already times the light's energy and its fog
 * energy, each shadow's lookup from view space, and an area light's rectangle and texture. One row
 * of texels per light.
 */
import * as THREE from 'three';
import { LIGHT_3D_DEFAULTS, SPOT_LIGHT_3D_DEFAULTS } from '../../godot/scene/light.js';
import { VOLUMETRIC_FOG_LIGHT_ENERGY_MIN, VOLUMETRIC_FOG_SHADOW_OPACITY_MIN, } from '../../godot/rendering/environment/volumetricFog.js';
import { areaLightShadowDepthScale } from '../../godot/rendering/lights/areaLightShadow.js';
import { DIRECTIONAL_SHADOW_MAX_SPLITS } from '../../godot/rendering/lights/directionalShadow.js';
import { lightBufferRange } from '../../godot/rendering/lights/lightBufferRange.js';
import { splitSunOf } from '../directionalShadow/splitSun.js';
import { AtlasAreaShadow } from '../positionalShadow/atlasAreaShadow.js';
import { AtlasOmniShadow } from '../positionalShadow/atlasOmniShadow.js';
import { AtlasSpotShadow } from '../positionalShadow/atlasSpotShadow.js';
import { visibleInTree } from '../visibleInTree.js';
import { readFogLightDeclaration } from './fogLightDeclaration.js';
import { AREA_LIGHT_AXES, areaLightViewAxis, readAreaLightFalloff } from '../areaLight/areaLightFalloff.js';
import { RowBuffer } from '../rowTexture/packedRows.js';
import { areaTexturePlacement } from '../areaLight/areaLightAtlas.js';
import { writeVec4 } from '../writeVec4.js';
/**
 * Where each value sits in a light's row, in texels of four floats. `CONE` holds the cone exponent,
 * the kind, the shadow opacity and a positional light's depth bias. `SHADOW_MATRICES` starts four
 * matrices of four columns: a directional light's splits, or one positional light's lookup.
 * `SHADOW_SPLITS` holds a directional light's split ends, or an omni or area light's paraboloid
 * slot. An area light's direction carries its inverse centre range where a spot holds its cone
 * cosine, and its width carries its texture's highest mip beside the texture's atlas rectangle.
 */
export const FogLightTexel = {
    POSITION: 0,
    COLOR: 1,
    DIRECTION: 2,
    CONE: 3,
    SHADOW_MATRICES: 4,
    SHADOW_SPLITS: 20,
    SHADOW_RANGES: 21,
    SHADOW_FADE: 22,
    AREA_WIDTH: 23,
    AREA_HEIGHT: 24,
    AREA_TEXTURE: 25,
};
/** Where each value sits in the cone texel, as an index into the row. */
const Cone = {
    EXPONENT: FogLightTexel.CONE * 4,
    KIND: FogLightTexel.CONE * 4 + 1,
    SHADOW_OPACITY: FogLightTexel.CONE * 4 + 2,
    SHADOW_BIAS: FogLightTexel.CONE * 4 + 3,
};
/** The texels in each row. */
export const FOG_LIGHT_TEXELS = FogLightTexel.AREA_TEXTURE + 1;
const ROW_LENGTH = FOG_LIGHT_TEXELS * 4;
/** The kind in a row's cone texel, which the fog shader branches on. */
export const FogLightKind = {
    DIRECTIONAL: 0,
    OMNI: 1,
    SPOT: 2,
    AREA: 3,
};
/** Scratch for one row, overwritten by each light. */
const position = new THREE.Vector3();
const towards = new THREE.Vector3();
const areaSize = new THREE.Vector2();
const axis = new THREE.Vector3();
const viewToShadow = new THREE.Matrix4();
const slotRectScratch = new THREE.Vector4();
const row = new Float32Array(ROW_LENGTH);
/**
 * The rows of every visible light the fog scatters, rewritten in place by each `pack`. three's
 * `intensity` is already Godot's `light_energy * PI` with its sign, its distance fade and the
 * render's exposure (`lightConstants.ts`, `sceneExposure.ts`), and its colour is linear, as in
 * Godot's buffer.
 */
export class FogLightRows extends RowBuffer {
    /** Whether a row's shadow reads the directional atlas, and whether one reads the positional atlas. */
    readsDirectionalAtlas = false;
    readsPositionalAtlas = false;
    constructor() {
        super(ROW_LENGTH);
    }
    /** Whether any row's shadow reads an atlas. */
    get readsShadowAtlas() {
        return this.readsDirectionalAtlas || this.readsPositionalAtlas;
    }
    /** Packs `lights` as `camera` sees them, and answers whether any row changed. */
    pack(lights, camera) {
        this.begin();
        this.readsDirectionalAtlas = false;
        this.readsPositionalAtlas = false;
        for (const light of lights) {
            if (!visibleInTree(light) || !writeRow(light, camera))
                continue;
            if (row[Cone.SHADOW_OPACITY] > VOLUMETRIC_FOG_SHADOW_OPACITY_MIN) {
                if (row[Cone.KIND] === FogLightKind.DIRECTIONAL)
                    this.readsDirectionalAtlas = true;
                else
                    this.readsPositionalAtlas = true;
            }
            this.append(row);
        }
        return this.finish();
    }
}
/** Writes `light`'s row into the scratch row, or answers false for a light the fog skips. */
function writeRow(light, camera) {
    const kind = fogLightKind(light);
    if (kind === null)
        return false;
    const declaration = readFogLightDeclaration(light);
    const fogEnergy = declaration?.energy ?? LIGHT_3D_DEFAULTS.volumetricFogEnergy;
    if (fogEnergy <= VOLUMETRIC_FOG_LIGHT_ENERGY_MIN)
        return false;
    row.fill(0);
    const energy = light.intensity * fogEnergy;
    writeVec4(row, FogLightTexel.COLOR, light.color.r * energy, light.color.g * energy, light.color.b * energy);
    row[Cone.KIND] = kind;
    if (kind === FogLightKind.DIRECTIONAL) {
        // Godot's directional row points towards the light, its basis +Z (`light_storage.cpp:640`).
        const { x, y, z } = targetDirection(light, camera).negate();
        writeVec4(row, FogLightTexel.DIRECTION, x, y, z);
        writeDirectionalShadow(light, camera);
        return true;
    }
    if (kind === FogLightKind.AREA)
        return writeArea(light, camera);
    const { x, y, z } = viewPosition(light, camera);
    const positional = light;
    // A GLB light keeps GLTFLoader's distance, 0 for a light with no `range`, so it needs the floor
    // that `positionalLightRange` gives an authored light.
    writeVec4(row, FogLightTexel.POSITION, x, y, z, 1 / lightBufferRange(positional.distance));
    row[FogLightTexel.COLOR * 4 + 3] = positional.decay;
    if (kind === FogLightKind.OMNI) {
        writeParaboloidShadow(light, camera);
        return true;
    }
    const spot = light;
    const travel = targetDirection(spot, camera);
    writeVec4(row, FogLightTexel.DIRECTION, travel.x, travel.y, travel.z, Math.cos(spot.angle));
    // The cone exponent is the inverse of `spot_angle_attenuation` (`light_storage.cpp:914`).
    row[Cone.EXPONENT] = 1 / (declaration?.spotAngleAttenuation ?? SPOT_LIGHT_3D_DEFAULTS.spotAngleAttenuation);
    writeSpotShadow(spot, camera);
    return true;
}
/**
 * An area light's range, attenuation, facing, rectangle, texture and shadow, as 4.7.2 packs them
 * (`light_storage.cpp:1059-1094`, `:1132-1141`, `:1150-1210`). Its rectangle ignores the node's scale, as Godot's does. A
 * light without a falloff, such as a GLB light, is left out.
 */
function writeArea(light, camera) {
    const falloff = readAreaLightFalloff(light);
    if (!falloff)
        return false;
    const range = lightBufferRange(falloff.range);
    const { x, y, z } = viewPosition(light, camera);
    writeVec4(row, FogLightTexel.POSITION, x, y, z, 1 / range);
    // The fog's falloff takes the attenuation less one (4.7.2 `volumetric_fog_process.glsl:653`).
    row[FogLightTexel.COLOR * 4 + 3] = falloff.attenuation - 1;
    const inverseCentreRange = areaLightShadowDepthScale(falloff.range, areaSize.set(light.width, light.height));
    writeViewAxis(FogLightTexel.DIRECTION, light, AREA_LIGHT_AXES.facing, 1, camera, inverseCentreRange);
    const { rect, maxMipmap } = areaTexturePlacement(light);
    writeVec4(row, FogLightTexel.AREA_TEXTURE, rect.x, rect.y, rect.width, rect.height);
    writeViewAxis(FogLightTexel.AREA_WIDTH, light, AREA_LIGHT_AXES.width, light.width, camera, maxMipmap);
    writeViewAxis(FogLightTexel.AREA_HEIGHT, light, AREA_LIGHT_AXES.height, light.height, camera);
    writeParaboloidShadow(light, camera);
    return true;
}
/** The light's `local` axis in view space, `length` long, in `texel` of the row. */
function writeViewAxis(texel, light, local, length, camera, w = 0) {
    areaLightViewAxis(axis, local, light, camera);
    writeVec4(row, texel, axis.x * length, axis.y * length, axis.z * length, w);
}
/**
 * A light in Godot's shadow list casts through its split sun (`../directionalShadow/splitSun.ts`).
 * Each slot maps view space into the atlas, and a slot past the last split repeats it, as its
 * matrix does. A depth range is its split's box, near to far (`light_storage.cpp:726`).
 */
function writeDirectionalShadow(light, camera) {
    const sun = splitSunOf(light);
    if (!sun)
        return;
    const { shadow } = sun;
    row[Cone.SHADOW_OPACITY] = shadow.intensity;
    for (let slot = 0; slot < DIRECTIONAL_SHADOW_MAX_SPLITS; slot++) {
        writeMatrix(slot, viewToShadow.multiplyMatrices(shadow.getMatrix(slot), camera.matrixWorld));
        const split = shadow.getCamera(Math.min(slot, shadow.splitCount - 1));
        row[FogLightTexel.SHADOW_SPLITS * 4 + slot] = shadow._cascadeData[slot].x;
        row[FogLightTexel.SHADOW_RANGES * 4 + slot] = split.far - split.near;
    }
    writeVec4(row, FogLightTexel.SHADOW_FADE, shadow.fade.from, shadow.fade.to, 0);
}
/**
 * An omni or area light with a slot looks up from its own space into its paraboloids' slot
 * (`../positionalShadow/atlasOmniShadow.ts`, `atlasAreaShadow.ts`).
 */
function writeParaboloidShadow(light, camera) {
    const { shadow } = light;
    if (!light.castShadow || !(shadow instanceof AtlasOmniShadow || shadow instanceof AtlasAreaShadow))
        return;
    const rect = shadow.slotRect(slotRectScratch);
    if (!rect)
        return;
    writeVec4(row, FogLightTexel.SHADOW_SPLITS, rect.x, rect.y, rect.z, rect.w);
    writePositionalShadow(shadow, light, camera);
}
/** A spot light with a slot looks up through its matrix into the slot (`../positionalShadow/atlasSpotShadow.ts`). */
function writeSpotShadow(light, camera) {
    const { shadow } = light;
    if (!light.castShadow || !(shadow instanceof AtlasSpotShadow))
        return;
    writePositionalShadow(shadow, light, camera);
}
function writePositionalShadow(shadow, light, camera) {
    row[Cone.SHADOW_OPACITY] = shadow.intensity;
    row[Cone.SHADOW_BIAS] = shadow.bias;
    writeMatrix(0, shadow.viewToLookup(camera, viewToShadow, light));
}
/** Writes `matrix` as the lookup of `slot`. */
function writeMatrix(slot, matrix) {
    row.set(matrix.elements, (FogLightTexel.SHADOW_MATRICES + slot * 4) * 4);
}
function fogLightKind(light) {
    if (light.isDirectionalLight)
        return FogLightKind.DIRECTIONAL;
    if (light.isSpotLight)
        return FogLightKind.SPOT;
    if (light.isPointLight)
        return FogLightKind.OMNI;
    if (light.isRectAreaLight)
        return FogLightKind.AREA;
    return null;
}
function viewPosition(light, camera) {
    return position.setFromMatrixPosition(light.matrixWorld).applyMatrix4(camera.matrixWorldInverse);
}
/** The world direction `light` shines along, from itself to its target, unnormalised, into `out`. */
export function shineDirection(light, out) {
    position.setFromMatrixPosition(light.matrixWorld);
    return out.setFromMatrixPosition(light.target.matrixWorld).sub(position);
}
/** The unit view-space direction a light shines along, from itself to its target. */
function targetDirection(light, camera) {
    return shineDirection(light, towards).transformDirection(camera.matrixWorldInverse);
}
//# sourceMappingURL=fogLights.js.map